import {
  Credentials,
  getShowLockedParam,
  showLockedPagesForCredentialType,
} from "../common/credentials.js";
import { APIResponse, ApiType, ResponseStatus, request } from "./api.js";
import { ApiError, UnknownError } from "./errors.js";
import { Page, PageListItem, PageSearchResultItem } from "./types/page.js";
import { captureException } from "../common/errors.js";

interface PageListSuccessResponse {
  pages: PageListItem[];
}

interface PageListOptionalParams {
  searchTerm: string;
  releaseId: number;
}

export async function listPages(
  credentials: Credentials,
  styleguideId: number,
  optionalParams: Partial<PageListOptionalParams> = {},
  clientUserAgent?: string,
) {
  try {
    const searchParams = new URLSearchParams({
      show_hidden: "false",
      show_locked: getShowLockedParam(credentials),
    });

    if (optionalParams.releaseId) {
      searchParams.set("release_id", optionalParams.releaseId.toString());
    }

    if (optionalParams.searchTerm) {
      searchParams.set("search", optionalParams.searchTerm);
    }

    const response = await request<
      APIResponse<PageListSuccessResponse, {}, {}>
    >(
      `/styleguides/${styleguideId}/pages`,
      credentials,
      {
        method: "GET",
      },
      searchParams,
      undefined,
      { clientUserAgent },
    );

    if ("pages" in response.data) {
      return response.data.pages;
    }

    throw new UnknownError();
  } catch (e) {
    captureException(e);
    return [];
  }
}

interface PageSuccessResponse {
  page: Page;
}

interface PageOptionalParams {
  releaseId: number;
}

export async function getPage(
  credentials: Credentials,
  pageId: number | string,
  optionalParams: Partial<PageOptionalParams> = {},
  clientUserAgent?: string,
) {
  try {
    const searchParams = new URLSearchParams();

    if (optionalParams.releaseId) {
      searchParams.set("release_id", optionalParams.releaseId.toString());
    }

    searchParams.set("show_locked", getShowLockedParam(credentials));

    const response = await request<APIResponse<PageSuccessResponse, {}, {}>>(
      `/pages/${pageId}`,
      credentials,
      {
        method: "GET",
      },
      searchParams,
      ApiType.OpenApiExperimental,
      { clientUserAgent },
    );

    if ("page" in response.data) {
      return response.data.page;
    }

    throw new UnknownError();
  } catch (e) {
    captureException(e);
    return null;
  }
}

interface SearchSuccessResponse {
  results: PageSearchResultItem[];
}

export async function searchPages(
  credentials: Credentials,
  styleguideId: number,
  query: string,
  releaseId?: number,
  clientUserAgent?: string,
) {
  const filters: Record<string, number | string | boolean> = {
    type: "page",
    styleguide_id: styleguideId,
  };

  if (typeof releaseId === "number") {
    filters["styleguide_version_id"] = releaseId;
  }

  if (showLockedPagesForCredentialType(credentials)) {
    filters["show_locked"] = true;
  }

  const response = await request<APIResponse<SearchSuccessResponse, {}, {}>>(
    `/search`,
    credentials,
    {
      method: "POST",
      body: JSON.stringify({
        query,
        filters,
        // needed for the rails-level check for allowing locked pages
        show_locked: getShowLockedParam(credentials),
      }),
    },
    undefined,
    undefined,
    { clientUserAgent },
  );

  if (response.status !== ResponseStatus.Success) {
    throw new ApiError(response.message);
  }

  if ("results" in response.data) {
    return response.data.results;
  }

  throw new UnknownError();
}
